import { computed, effectScope, readonly, ref, watch } from 'vue'
import { useBetaAnnouncement, useFeatureFlag } from '@/composables'
import { useUserStore } from '@/store/userStore'
import {
  AiAssistantBetaService,
  FlagEnum,
  ResolutionEnum,
  RoleEnum,
  type AiAssistantBetaStateResponse
} from '@/types/generated'

export type FeatureId = 'assign_law_fields' | 'assign_clerks' | 'suggest_first_client_email'

export type DeactivationReason = 'doesnt_fit_workflow' | 'inaccurate_suggestions' | 'other'

// Backend-backed state — populated by hydrateState(), then mutated only via the dedicated
// activate / setActivatedFeature / deactivate functions which post to the API and replace
// state from the response.
const isModalOpen = ref(false)
const isActivated = ref(false)
const activatedFeatures = ref<FeatureId[]>([])
const isHydrated = ref(false)
const isLoading = ref(false)

// Generation counter — incremented on every resetState() so in-flight async calls
// (hydrateState, activate, setActivatedFeature, deactivate) that started under a
// previous generation silently discard their response instead of overwriting state
// that now belongs to a different user.
let stateGeneration = 0

// Feature flag — gates the entire AI Assistant V2 Beta surface (modal, banner, sidepanel tab).
// useFeatureFlag has its own 30s cache; we let it manage refresh cadence rather than guarding
// here, so a stale local value gets refreshed on subsequent composable calls.
const aiAssistantBetaFlag = useFeatureFlag(FlagEnum.AI_ASSISTANT_V2_BETA)

// One-time setup of the user-uuid reset watcher. The activation state is module-scoped, so
// without this hook a state hydrated for user A would silently apply to user B when they log
// in to the same tab without a hard reload (shared workstations, account switching, E2E
// fixture swaps). Set up on the first composable invocation only — multiple consumer mounts
// shouldn't each register their own redundant watcher.
let userResetWatcherInitialized = false

// Detached effect scope so the watcher's lifetime is tied to this module, not to whichever
// component happened to be the first composable consumer. Without this, the watcher would
// inherit the active setup() scope at first call — fine while the first consumer is a
// long-lived root (App.vue today), but a silent footgun if mount order ever changes.
const userResetScope = effectScope(true)

function resetState() {
  stateGeneration++
  isModalOpen.value = false
  isActivated.value = false
  activatedFeatures.value = []
  isHydrated.value = false
  hydratePromise = null
}

function applyServerState(state: AiAssistantBetaStateResponse) {
  isActivated.value = state.is_activated
  activatedFeatures.value = state.activated_skills as FeatureId[]
  isHydrated.value = true
}

function ensureUserResetWatcher(userStore: ReturnType<typeof useUserStore>) {
  if (userResetWatcherInitialized) return
  userResetWatcherInitialized = true
  // Watcher fires on changes only (not on the initial value), so the first user load is a
  // no-op against the already-default state. Logout (uuid → undefined) and login (undefined →
  // new uuid) both trigger a wipe — by the time the new user is set, state is clean.
  // Announcement state is reset by useBetaAnnouncement's own watcher.
  userResetScope.run(() => {
    watch(
      () => userStore.user?.uuid,
      () => resetState()
    )
  })
}

let hydratePromise: Promise<void> | null = null

// Gate the network hydrate behind the feature flag so users without AI_ASSISTANT_V2_BETA
// don't fire GET /api/v3/ai_assistant_beta/state/ on every page that mounts a consumer
// (App.vue at root, the case-list banner, the side-panel tab, etc.).
async function hydrateIfFlagEnabled(announcement: ReturnType<typeof useBetaAnnouncement>) {
  await aiAssistantBetaFlag.checkFlag()
  if (!aiAssistantBetaFlag.isEnabled.value) return
  if (isHydrated.value && announcement.isHydrated.value) return
  // Hydrate org-level state and per-user announcement state in parallel — they are
  // independent reads against the same authenticated session.
  void Promise.all([hydrateState(), announcement.hydrate()])
}

async function hydrateState() {
  if (isHydrated.value || hydratePromise) return hydratePromise ?? Promise.resolve()
  const gen = stateGeneration
  isLoading.value = true
  hydratePromise = (async () => {
    try {
      const state = await AiAssistantBetaService.aiAssistantBetaStateRetrieve()
      if (gen !== stateGeneration) return
      applyServerState(state)
    } catch (error) {
      console.error('[AiAssistantBeta] hydrateState() failed', error)
      if (gen !== stateGeneration) return
      // Mark as hydrated even on failure so rapid component mounts don't fire
      // repeated requests when the backend is down. The state stays at defaults
      // (not activated, no skills) which is a safe fallback. A full page reload
      // or user switch (resetState) will clear isHydrated and retry.
      isHydrated.value = true
    } finally {
      if (gen === stateGeneration) {
        isLoading.value = false
      }
      hydratePromise = null
    }
  })()
  return hydratePromise
}

export function useAiAssistantBeta() {
  const userStore = useUserStore()
  const announcement = useBetaAnnouncement()
  ensureUserResetWatcher(userStore)
  // Apply announcement state (and trigger the first hydrate) when this composable is first
  // consumed by a logged-in user. The hydrate is gated on the feature flag inside
  // hydrateIfFlagEnabled — see comment there. Subsequent calls short-circuit via
  // isHydrated / hydratePromise.
  if (userStore.user?.uuid) {
    if (!isHydrated.value || !announcement.isHydrated.value) {
      void hydrateIfFlagEnabled(announcement)
    }
  }

  const isAdmin = computed(() => userStore.user?.role === RoleEnum.ADMIN)
  const isFeatureEnabled = computed(() => aiAssistantBetaFlag.isEnabled.value)

  const shouldShowModalOnMount = computed(
    () =>
      isHydrated.value &&
      announcement.isHydrated.value &&
      isFeatureEnabled.value &&
      isAdmin.value &&
      !isActivated.value &&
      announcement.resolution.value === ResolutionEnum.MODAL
  )

  // Side panel tab is the gateway to the AI Assistant feature itself, not just an announcement.
  // Visible to everyone with the flag on — non-admins see the activation UI in a read-only
  // state with an "only admins can change settings" banner. Activation announcements (modal,
  // banner) remain admin-only via shouldShowModalOnMount / shouldShowBanner.
  const shouldShowSidePanelTab = computed(() => isFeatureEnabled.value)

  const shouldShowBanner = computed(
    () =>
      isHydrated.value &&
      announcement.isHydrated.value &&
      isFeatureEnabled.value &&
      isAdmin.value &&
      !isActivated.value &&
      announcement.resolution.value === ResolutionEnum.BANNER
  )

  function openModal() {
    isModalOpen.value = true
  }

  async function closeModal() {
    isModalOpen.value = false
    // Persist server-side; failure is non-fatal — the modal stays closed for this session
    // and the next hydrate (page reload, tab switch) will re-resolve from the source of
    // truth. Catch keeps an unhandled rejection out of the console for what is a UX-only
    // signal.
    try {
      await announcement.markModalSeen()
    } catch {
      /* noop — see comment above */
    }
  }

  async function dismissBanner() {
    try {
      await announcement.dismissBanner()
    } catch {
      /* noop — see comment in closeModal() */
    }
  }

  async function activate(features: FeatureId[]) {
    // Defense-in-depth: only admins can flip activation, regardless of which surface
    // called us. The trigger paths (modal CTA, settings page, sidepanel view) all gate
    // their own UI on isAdmin, but we also enforce here so a misbehaving caller can't
    // bypass the role check.
    if (!isAdmin.value) return
    const gen = stateGeneration
    isLoading.value = true
    try {
      const state = await AiAssistantBetaService.aiAssistantBetaActivateCreate({
        requestBody: { skills: features }
      })
      if (gen !== stateGeneration) return
      applyServerState(state)
      isModalOpen.value = false
    } catch (error) {
      console.error('[AiAssistantBeta] activate() failed', error)
      throw error
    } finally {
      if (gen === stateGeneration) {
        isLoading.value = false
      }
    }
  }

  async function setActivatedFeature(id: FeatureId, enabled: boolean) {
    // Defense-in-depth: same admin gate as activate(). Settings UI hides the toggles
    // for non-admins, but a misbehaving caller (or a stale UI state) must not be able
    // to flip per-feature flags.
    if (!isAdmin.value) return
    // Only meaningful when already activated — feature toggling is the long-term
    // settings surface. Pre-activation selection is component-local.
    if (!isActivated.value) return
    const gen = stateGeneration
    isLoading.value = true
    try {
      const state = await AiAssistantBetaService.aiAssistantBetaSkillsPartialUpdate({
        skillId: id,
        requestBody: { enabled }
      })
      if (gen !== stateGeneration) return
      applyServerState(state)
    } catch (error) {
      console.error('[AiAssistantBeta] setActivatedFeature() failed', error)
      throw error
    } finally {
      if (gen === stateGeneration) {
        isLoading.value = false
      }
    }
  }

  async function deactivate(reason: DeactivationReason, feedback?: string) {
    // Defense-in-depth: same admin gate as activate(). The deactivation dialog is only
    // mounted from admin surfaces, but we enforce here too so the role check can't be
    // bypassed by a stray caller.
    if (!isAdmin.value) return
    const gen = stateGeneration
    isLoading.value = true
    try {
      const state = await AiAssistantBetaService.aiAssistantBetaDeactivateCreate({
        requestBody: { reason, feedback: feedback ?? '' }
      })
      if (gen !== stateGeneration) return
      applyServerState(state)
    } catch (error) {
      console.error('[AiAssistantBeta] deactivate() failed', error)
      throw error
    } finally {
      if (gen === stateGeneration) {
        isLoading.value = false
      }
    }
  }

  // Wrap mutable state with readonly() so consumers can only mutate via the dedicated
  // functions below — protects invariants like "closeModal sets hasSeenModal=true" from
  // being bypassed by a v-model write or a stray .value = X.
  return {
    isAdmin,
    isModalOpen: readonly(isModalOpen),
    isActivated: readonly(isActivated),
    isFeatureEnabled,
    activatedFeatures: readonly(activatedFeatures),
    isHydrated: readonly(isHydrated),
    isLoading: readonly(isLoading),
    shouldShowModalOnMount,
    shouldShowSidePanelTab,
    shouldShowBanner,
    openModal,
    closeModal,
    dismissBanner,
    activate,
    setActivatedFeature,
    deactivate
  }
}
